import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TYPE } from '@/theme/typography';
import { DEFAULT_PALETTE, getPaletteByKey, getSidebarColors, type SidebarColors } from '@/config/colorPalettes';
import { useUIStore, type NavStyle } from '@/stores/uiStore';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  Tooltip,
  Collapse,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  RocketLaunch as RocketLaunchIcon,
  Person as ProfileIcon,
  TrendingUp as RevenueIcon,
  TrendingDown as ExpenseIcon,
  People as HRIcon,
  Payments as PayrollIcon,
  Apartment as TenantIcon,
  Settings as SettingsIcon,
  AccountBalance as LedgerIcon,
  BarChart as BudgetIcon,
  Business as GroupIcon,
  SmartToy as AIIcon,
  EventNote as LeaveIcon,
  Campaign as PortalIcon,
  GavelRounded as ApprovalsIcon,
  ContactPage as DirectoryIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  PersonSearch as RecruitmentIcon,
  EmojiEvents as PerformanceIcon,
  HowToReg as OnboardingIcon,
  HelpOutline as HelpCenterIcon,
  Assessment as ReportsIcon,
  ViewList as ViewListIcon,
  AccountTree as AccountTreeIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as DisbursementIcon,
  Handshake as CRMIcon,
  ViewKanban as KanbanIcon,
  PeopleOutline as ContactsIcon,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useFilterStore } from '@/stores/filterStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { authApi } from '@/api/endpoints/auth';
import { approvalsApi } from '@/api/endpoints/approvals';
import { setCompanySwitchInProgress } from '@/App';
import { resolveUploadUrl } from '@/utils/format';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant: 'permanent' | 'temporary';
  collapsed?: boolean;
  companyColour?: string;
}

// Getting Started / Workflow Journeys nav entry is flag-gated.
const journeysEnabled = import.meta.env.VITE_JOURNEYS_ENABLED === 'true';

const DRAWER_FULL = 260;
const DRAWER_MINI = 68;

interface NavItemProps {
  label: string;
  path: string;
  icon: React.ReactNode;
  collapsed: boolean;
  isActive: boolean;
  onClick: () => void;
  tooltip?: string;
  /** If true, renders expand/collapse chevron and calls onToggle */
  hasChildren?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  /** Indented child item */
  isChild?: boolean;
  /** Computed sidebar colors */
  sc?: SidebarColors;
}

function NavItem({ label, icon, collapsed, isActive, onClick, tooltip, hasChildren, expanded, onToggle, isChild, sc }: NavItemProps) {
  return (
    <Tooltip
      title={tooltip || (collapsed ? label : '')}
      placement="right"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            fontSize: TYPE.sm,
            fontWeight: TYPE.medium,
            py: 0.5,
            px: 1.2,
          },
        },
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, -4] } }],
        },
      }}
    >
      <ListItemButton
        onClick={onClick}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          borderRadius: 1,
          mb: 0,
          mx: collapsed ? 0.5 : 0,
          px: collapsed ? 0 : 1,
          pl: !collapsed && isChild ? 2.5 : undefined,
          py: 0.35,
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderLeft: collapsed ? 'none' : (isActive ? '3px solid' : '3px solid transparent'),
          borderColor: isActive ? (sc?.activeText ?? 'primary.main') : 'transparent',
          bgcolor: isActive ? (sc?.activeBg ?? 'action.selected') : 'transparent',
          '&:hover': { bgcolor: sc?.hoverBg ?? 'action.hover' },
          minHeight: 30,
        }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 'auto' : 32, justifyContent: 'center', color: isActive ? (sc?.activeText ?? 'primary.main') : (sc?.textSecondary ?? 'text.secondary') }}>
          {icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: isChild ? TYPE.sm : '0.891rem',
              fontWeight: isActive ? TYPE.semibold : TYPE.medium,
              color: isActive ? (sc?.activeText ?? 'primary.main') : (sc?.textSecondary ?? 'text.secondary'),
            }}
          />
        )}
        {!collapsed && hasChildren && (
          <IconButton
            size="small"
            aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            sx={{
              p: 0.25,
              color: sc?.textSecondary ?? 'text.secondary',
              transition: 'transform 0.2s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </ListItemButton>
    </Tooltip>
  );
}


export default function Sidebar({
  open,
  onClose,
  variant,
  collapsed = false,
  companyColour = DEFAULT_PALETTE.primary
}: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  // user subscription removed in Sprint 4b (only consumed is_system_admin).
  const companies = useAuthStore((s) => s.companies);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);
  const { year, setYear } = useFilterStore();

  // Approval badge count
  const { data: approvalStats } = useQuery({
    queryKey: ['approvals/stats'],
    queryFn: () => approvalsApi.stats(),
    select: (res) => res.data,
    refetchInterval: 60_000, // refresh every 60s
    enabled: !!activeCompanyId,
  });
  const pendingApprovals = approvalStats?.pending || 0;

  const isSystemAdmin = false; // Sprint 4b: removed; permissions are now purely role + perm-code driven
  const isAdmin = isSystemAdmin || Boolean(activeCompany?.is_group_level);
  const isStaffOnly = !isSystemAdmin && activeCompany?.role_name === 'Staff';
  const enabledModules = useAuthStore((s) => s.getEnabledModules());
  const isModuleEnabled = (key: string) => enabledModules.includes(key);

  // Permission-based filtering: check user's actual permissions
  const permissionSet = new Set(activeCompany?.permissions ?? []);
  const hasPerm = (code: string): boolean => {
    if (isSystemAdmin) return true;
    return permissionSet.has(code);
  };
  const hasAnyPerm = (codes: string[]): boolean => {
    if (isSystemAdmin) return true;
    return codes.some((c) => permissionSet.has(c));
  };

  const isActive = (path: string) => {
    // Hash-based match for hub shortcuts (e.g. /finance#revenue)
    if (path.includes('#')) {
      const [base, hash] = path.split('#');
      return location.pathname === base && location.hash === `#${hash}`;
    }
    // Exact match for dashboard
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    // Prefix match for sub-routes (e.g. /finance matches /finance#revenue)
    return location.pathname.startsWith(path);
  };

  const handleNav = (path: string, parentKey?: string) => {
    setExpandedSections((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((k) => { next[k] = false; });
      if (parentKey) next[parentKey] = true;
      return next;
    });
    navigate(path);
    if (variant === 'temporary') onClose();
  };

  const [switching, setSwitching] = useState(false);
  const { showError } = useNotificationStore();

  const handleCompanySwitch = async (companyId: string) => {
    if (companyId === activeCompanyId || switching) return;
    setSwitching(true);
    setCompanySwitchInProgress(true);
    try {
      const res = await authApi.switchCompany(companyId);
      // Remove all cached queries BEFORE updating state — prevents stale
      // cross-company data from being served during the transition.
      queryClient.removeQueries();
      // Batch: update modules, permissions, and switch company atomically.
      // Always write modules/permissions from response (even if empty array)
      // to avoid stale data from previous company persisting.
      useAuthStore.setState((state) => ({
        activeCompanyId: companyId,
        companies: state.companies.map((c) =>
          c.company_id === companyId
            ? {
                ...c,
                enabled_modules: res.data.enabled_modules,
                permissions: res.data.permissions,
              }
            : c
        ),
      }));
    } catch {
      showError('Failed to switch company. Please try again.');
    } finally {
      setSwitching(false);
      setCompanySwitchInProgress(false);
    }
  };

  const sidebarWidth = collapsed ? DRAWER_MINI : DRAWER_FULL;

  // Sidebar color scheme
  const sidebarStyle = useUIStore((s) => s.sidebarStyle);
  const darkMode = useUIStore((s) => s.darkMode);
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const paletteKey = useUIStore((s) => s.groupPaletteKey);
  const palette = getPaletteByKey(paletteKey);
  const sc = getSidebarColors(palette, sidebarStyle, darkMode);
  const navStyle = useUIStore((s) => s.navStyle);
  const setNavStyle = useUIStore((s) => s.setNavStyle);
  // NAV-001: flip the local store instantly (snappy UI), then persist the
  // choice to the server so it survives re-login + follows the user across
  // devices. Fire-and-forget — a failed PATCH leaves the local choice intact.
  const handleSetNavStyle = (style: NavStyle) => {
    setNavStyle(style);
    authApi.updatePreferences({ nav_style: style }).catch(() => {
      /* non-fatal: local preference still applied */
    });
  };

  // Helper: should a nav section be visible?
  // Must have the module enabled AND at least one relevant permission
  const showFinance = isModuleEnabled('accounting') && hasAnyPerm([
    'accounting.revenue.read', 'accounting.expense.read',
    'accounting.journal.read', 'accounting.reports.read',
  ]);
  const showBudgetInFinance = isModuleEnabled('budget') && hasAnyPerm([
    'budget.read', 'budget.write',
  ]);
  const showFacility = isModuleEnabled('tenants') && hasAnyPerm([
    'tenants.read', 'tenants.write',
  ]);
  const showPeople = isModuleEnabled('hr') && hasAnyPerm([
    'hr.employees.read', 'hr.employees.write', 'hr.leave.read',
  ]);
  const showOnboarding = isModuleEnabled('onboarding') && hasAnyPerm([
    'hr.onboarding.read', 'hr.onboarding.write', 'onboarding.read', 'onboarding.write',
  ]);
  const showPayroll = isModuleEnabled('payroll') && hasAnyPerm([
    'payroll.read', 'payroll.write',
  ]);
  const showRecruitment = isModuleEnabled('recruitment') && hasAnyPerm([
    'hr.recruitment.read', 'hr.recruitment.write', 'recruitment.read', 'recruitment.write',
  ]);
  const showPerformance = isModuleEnabled('performance') && hasAnyPerm([
    'performance.reviews.read', 'performance.goals.read',
    'performance.reviews.write', 'performance.goals.write',
  ]);
  const showGroup = isAdmin && isModuleEnabled('group');
  const showCRM = isModuleEnabled('crm') && hasAnyPerm(['crm.read', 'crm.write']);
  const showAI = isModuleEnabled('ai') && hasAnyPerm([
    'ai.chat', 'ai.analytics', 'ai.settings',
  ]);
  // Reports hub — visible whenever the user can see ANY report-source hub.
  // Reuses the hub gates above so Reports never hides from someone who can
  // reach Finance/Payroll/People/Intelligence. Per-card gating happens inside
  // the hub (useVisibleReports), which also renders a friendly empty state.
  const showReports =
    showFinance || showBudgetInFinance || showPayroll || showPeople ||
    showAI || showGroup || hasPerm('audit.read');
  const showSettings = hasPerm('settings.read') || isAdmin;

  // Collapsible section state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    finance: false,
    people: false,
    payroll: false,
    crm: false,
  });
  const toggleSection = (key: string) =>
    setExpandedSections((prev) => {
      const isOpen = prev[key];
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((k) => { next[k] = false; });
      next[key] = !isOpen;
      return next;
    });

  const sidebarBoxSx = {
    width: variant === 'temporary' ? DRAWER_FULL : sidebarWidth,
    flexShrink: 0,
    transition: 'width 0.3s ease, background-color 0.3s ease',
    bgcolor: sc.bg,
    borderTop: `4px solid ${companyColour}`,
    borderRight: '1px solid',
    borderRightColor: sc.divider,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
  };

  const navContent = (
    <Box component="nav" aria-label="Main navigation" sx={sidebarBoxSx}>
      {/* Logo area */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: collapsed ? 'center' : 'flex-start', minHeight: 65, borderBottom: '1px solid', borderColor: sc.divider }}>
        {(activeCompany?.group_logo_url || activeCompany?.company_logo_url) ? (
          <Box
            component="img"
            src={resolveUploadUrl(activeCompany?.group_logo_url || activeCompany?.company_logo_url)}
            alt={activeCompany?.group_name || activeCompany?.company_name || 'Logo'}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: TYPE.bold,
              fontSize: TYPE.lg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {(activeCompany?.group_name || activeCompany?.company_name || 'ERP').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
          </Box>
        )}
        {!collapsed && (
          <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap', opacity: 1, transition: 'opacity 0.2s' }}>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, color: sc.text, lineHeight: 1.3 }}>
              {companies.length === 1
                ? (activeCompany?.company_name || 'Company')
                : (activeCompany?.group_name || activeCompany?.company_name || 'Company')}
            </Typography>
            <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium, color: sc.textSecondary }}>
              {companies.length > 1 && activeCompany?.group_name ? 'Organization' : 'ERP'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Context Panel — Company / Year — hidden when collapsed */}
      {!collapsed && (
        <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1, borderBottom: '1px solid', borderColor: sc.divider }}>
          <Box>
            <Typography sx={{ fontSize: TYPE.xs, fontWeight: TYPE.bold, letterSpacing: '0.06em', textTransform: 'uppercase', color: sc.sectionLabel, mb: 0.5 }}>
              Company
            </Typography>
            {companies.length > 1 ? (
              <FormControl fullWidth size="small">
                <Select
                  value={activeCompanyId || ''}
                  onChange={(e) => handleCompanySwitch(e.target.value as string)}
                  disabled={switching}
                  inputProps={{ 'aria-label': 'Select company' }}
                  sx={{
                    fontSize: TYPE.md, fontWeight: TYPE.medium,
                    color: sc.text,
                    '.MuiOutlinedInput-notchedOutline': { borderColor: sc.divider },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: sc.textSecondary },
                    '.MuiSvgIcon-root': { color: sc.textSecondary },
                  }}
                  renderValue={() => activeCompany?.company_name || 'Select company'}
                >
                  {companies.map((c) => (
                    <MenuItem key={c.company_id} value={c.company_id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.company_colour || DEFAULT_PALETTE.primary, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: TYPE.md }}>{c.company_name}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.medium, color: sc.text }}>
                {activeCompany?.company_name || 'Company'}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography sx={{ fontSize: TYPE.xs, fontWeight: TYPE.bold, letterSpacing: '0.06em', textTransform: 'uppercase', color: sc.sectionLabel, mb: 0.5 }}>
              Year
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                inputProps={{ 'aria-label': 'Select fiscal year' }}
                sx={{
                  fontSize: TYPE.md, fontWeight: TYPE.medium,
                  color: sc.text,
                  '.MuiOutlinedInput-notchedOutline': { borderColor: sc.divider },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: sc.textSecondary },
                  '.MuiSvgIcon-root': { color: sc.textSecondary },
                }}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Global search — relocated here, below the Year selector (left rail).
              Opens the Cmd+K command palette. */}
          <Box
            component="button"
            type="button"
            onClick={() => setCommandPaletteOpen(true)}
            aria-label="Open search"
            sx={{
              mt: 0.5,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              cursor: 'pointer',
              bgcolor: 'transparent',
              border: '1px solid',
              borderColor: sc.divider,
              color: sc.textSecondary,
              transition: 'border-color 0.15s, color 0.15s',
              '&:hover': { borderColor: sc.textSecondary, color: sc.text },
            }}
          >
            <SearchIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <Typography sx={{ fontSize: TYPE.md, flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Search…
            </Typography>
            <Box sx={{ fontSize: TYPE.xs, fontWeight: TYPE.semibold, border: '1px solid', borderColor: sc.divider, borderRadius: '4px', px: 0.5, flexShrink: 0 }}>
              ⌘K
            </Box>
          </Box>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: collapsed ? 0.5 : 1.5, pt: 2.5 }}>
        {navStyle === 'flat' ? (
          /* ── FLAT MODE (default) — unchanged single-column layout ── */
          <>
            <List disablePadding>
              {!isStaffOnly && (
                <NavItem label={t('nav.dashboard')} path="/dashboard" icon={<DashboardIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/dashboard')} onClick={() => handleNav('/dashboard')} tooltip={`Dashboard for ${activeCompany?.company_name || 'company'}`} sc={sc} />
              )}
              {journeysEnabled && (Boolean(activeCompany?.is_group_level) || activeCompany?.role_name === 'Company Administrator') && (
                <NavItem label="Getting started" path="/getting-started" icon={<RocketLaunchIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/getting-started')} onClick={() => handleNav('/getting-started')} tooltip="Guided workflows to set up and run HR" sc={sc} />
              )}
              <NavItem label="Portal" path="/portal" icon={<PortalIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/portal')} onClick={() => handleNav('/portal')} sc={sc} />
            </List>
            <Divider sx={{ my: 2, mx: collapsed ? 0.5 : 1, borderColor: sc.divider }} />
            <List disablePadding>
              {isModuleEnabled('approvals') && (
                <NavItem
                  label="Approvals"
                  path="/approvals"
                  icon={
                    <Badge badgeContent={pendingApprovals} color="error" max={99} overlap="circular"
                      sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, p: '0 4px' } }}
                    >
                      <ApprovalsIcon fontSize="small" />
                    </Badge>
                  }
                  collapsed={collapsed}
                  isActive={isActive('/approvals')}
                  onClick={() => handleNav('/approvals')}
                  sc={sc}
                />
              )}
            </List>

            {/* ── Organization ── (hub, admin only, Business+ tier) */}
            {showGroup && (
              <List disablePadding>
                <NavItem label="Organization" path="/group" icon={<GroupIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/group')} onClick={() => handleNav('/group')} tooltip={`${activeCompany?.group_name || 'Organization'}`} sc={sc} />
              </List>
            )}

            {/* ── Finance ── (hub + dropdown children) */}
            {(showFinance || showBudgetInFinance) && (
              <List disablePadding>
                <NavItem
                  label="Finance" path="/finance" icon={<LedgerIcon fontSize="small" />}
                  collapsed={collapsed} isActive={isActive('/finance')}
                  onClick={() => handleNav('/finance')}
                  hasChildren={!collapsed}
                  expanded={expandedSections.finance}
                  onToggle={() => toggleSection('finance')}
                  sc={sc}
                />
                {!collapsed && (
                  <Collapse in={expandedSections.finance} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {showFinance && hasAnyPerm(['accounting.revenue.read', 'accounting.revenue.write']) && (
                        <NavItem label="Revenue" path="/finance#transactions.revenue" icon={<RevenueIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#transactions.revenue')} onClick={() => handleNav('/finance#transactions.revenue', 'finance')} isChild sc={sc} />
                      )}
                      {showFinance && hasAnyPerm(['accounting.expense.read', 'accounting.expense.write']) && (
                        <NavItem label="Expenses" path="/finance#transactions.expenses" icon={<ExpenseIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#transactions.expenses')} onClick={() => handleNav('/finance#transactions.expenses', 'finance')} isChild sc={sc} />
                      )}
                      {showBudgetInFinance && (
                        <NavItem label="Budget" path="/finance#budget" icon={<BudgetIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#budget')} onClick={() => handleNav('/finance#budget', 'finance')} isChild sc={sc} />
                      )}
                      {hasAnyPerm(['billing.read']) && (
                        <NavItem label="Payments" path="/finance#payments" icon={<RevenueIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#payments')} onClick={() => handleNav('/finance#payments', 'finance')} isChild sc={sc} />
                      )}
                      {hasAnyPerm(['billing.read']) && (
                        <NavItem label="Payouts" path="/finance#payouts" icon={<RevenueIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#payouts')} onClick={() => handleNav('/finance#payouts', 'finance')} isChild sc={sc} />
                      )}
                    </List>
                  </Collapse>
                )}
              </List>
            )}

            {/* ── Facility ── (hub, no shortcuts) */}
            {showFacility && (
              <List disablePadding>
                <NavItem label="Facility" path="/facility" icon={<TenantIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/facility')} onClick={() => handleNav('/facility')} sc={sc} />
              </List>
            )}

            {/* ── People ── (hub + dropdown children, includes onboarding, recruitment, performance) */}
            {(showPeople || showOnboarding || showRecruitment || showPerformance) && (
              <List disablePadding>
                <NavItem
                  label="People" path="/people" icon={<HRIcon fontSize="small" />}
                  collapsed={collapsed} isActive={isActive('/people')}
                  onClick={() => handleNav('/people')}
                  hasChildren={!collapsed && (showPeople || showOnboarding || showRecruitment || showPerformance)}
                  expanded={expandedSections.people}
                  onToggle={() => toggleSection('people')}
                  sc={sc}
                />
                {!collapsed && (
                  <Collapse in={expandedSections.people} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {showPeople && hasAnyPerm(['hr.employees.read', 'hr.employees.write']) && (
                        <NavItem label="Employees" path="/people#workforce.employees" icon={<DirectoryIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#workforce.employees')} onClick={() => handleNav('/people#workforce.employees', 'people')} isChild sc={sc} />
                      )}
                      {showPeople && hasAnyPerm(['hr.leave.read', 'hr.leave.write']) && (
                        <NavItem label="Leave" path="/people#management.leave" icon={<LeaveIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#management.leave')} onClick={() => handleNav('/people#management.leave', 'people')} isChild sc={sc} />
                      )}
                      {showOnboarding && (
                        <NavItem label="Onboarding" path="/people#hiring.onboarding" icon={<OnboardingIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#hiring.onboarding')} onClick={() => handleNav('/people#hiring.onboarding', 'people')} isChild sc={sc} />
                      )}
                      {showRecruitment && (
                        <NavItem label="Recruitment" path="/people#hiring.recruitment" icon={<RecruitmentIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#hiring.recruitment')} onClick={() => handleNav('/people#hiring.recruitment', 'people')} isChild sc={sc} />
                      )}
                      {showPerformance && (
                        <NavItem label="Performance" path="/people#management.performance" icon={<PerformanceIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#management.performance')} onClick={() => handleNav('/people#management.performance', 'people')} isChild sc={sc} />
                      )}
                    </List>
                  </Collapse>
                )}
              </List>
            )}

            {/* ── Payroll ── (hub + WHT Remittance + Disbursement Center) */}
            {showPayroll && (
              <List disablePadding>
                <NavItem label="Payroll" path="/payroll" icon={<PayrollIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/payroll')} onClick={() => handleNav('/payroll')} sc={sc} />
                {hasAnyPerm(['payroll.read', 'payroll.write']) && (
                  <NavItem label="WHT Remittance" path="/payroll/wht-remittance" icon={<ReceiptIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/payroll/wht-remittance')} onClick={() => handleNav('/payroll/wht-remittance')} isChild sc={sc} />
                )}
                {hasAnyPerm(['payroll.read', 'payroll.write']) && (
                  <NavItem label="Disbursement Center" path="/payroll/disbursement-center" icon={<DisbursementIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/payroll/disbursement-center')} onClick={() => handleNav('/payroll/disbursement-center')} isChild sc={sc} />
                )}
              </List>
            )}

            {/* ── CRM ── (contacts + deals kanban) */}
            {showCRM && (
              <List disablePadding>
                <NavItem
                  label="CRM" path="/crm/contacts" icon={<CRMIcon fontSize="small" />}
                  collapsed={collapsed} isActive={isActive('/crm')}
                  onClick={() => handleNav('/crm/contacts')}
                  hasChildren={!collapsed}
                  expanded={expandedSections.crm}
                  onToggle={() => toggleSection('crm')}
                  sc={sc}
                />
                {!collapsed && (
                  <Collapse in={expandedSections.crm} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      <NavItem label="Contacts" path="/crm/contacts" icon={<ContactsIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/crm/contacts')} onClick={() => handleNav('/crm/contacts', 'crm')} isChild sc={sc} />
                      <NavItem label="Deals" path="/crm/kanban" icon={<KanbanIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/crm/kanban')} onClick={() => handleNav('/crm/kanban', 'crm')} isChild sc={sc} />
                    </List>
                  </Collapse>
                )}
              </List>
            )}

            {/* ── Intelligence ── (hub, no shortcuts) */}
            {showAI && (
              <List disablePadding>
                <NavItem label="Intelligence" path="/intelligence" icon={<AIIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/intelligence')} onClick={() => handleNav('/intelligence')} sc={sc} />
              </List>
            )}

            {/* ── Reports ── (unified report launcher) */}
            {showReports && (
              <List disablePadding>
                <NavItem label="Reports" path="/reports" icon={<ReportsIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/reports')} onClick={() => handleNav('/reports')} sc={sc} />
              </List>
            )}

            {/* ── System ── */}
            {showSettings && (
              <List disablePadding>
                <NavItem label="Settings" path="/settings" icon={<SettingsIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/settings')} onClick={() => handleNav('/settings')} sc={sc} />
              </List>
            )}
          </>
        ) : (
          /* ── GROUPED MODE — 6 labelled zones ── */
          <>
            {/* Zone 1: Workspace — Portal is always visible, so this zone always renders */}
            <>
              {!collapsed && (
                <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'text.disabled', mt: 1.5, mb: 0.5, px: 2 }}>
                  Workspace
                </Typography>
              )}
              <List disablePadding>
                {!isStaffOnly && (
                  <NavItem label={t('nav.dashboard')} path="/dashboard" icon={<DashboardIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/dashboard')} onClick={() => handleNav('/dashboard')} tooltip={`Dashboard for ${activeCompany?.company_name || 'company'}`} sc={sc} />
                )}
                {journeysEnabled && (Boolean(activeCompany?.is_group_level) || activeCompany?.role_name === 'Company Administrator') && (
                  <NavItem label="Getting started" path="/getting-started" icon={<RocketLaunchIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/getting-started')} onClick={() => handleNav('/getting-started')} tooltip="Guided workflows to set up and run HR" sc={sc} />
                )}
                <NavItem label="Portal" path="/portal" icon={<PortalIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/portal')} onClick={() => handleNav('/portal')} sc={sc} />
                {isModuleEnabled('approvals') && (
                  <NavItem
                    label="Approvals"
                    path="/approvals"
                    icon={
                      <Badge badgeContent={pendingApprovals} color="error" max={99} overlap="circular"
                        sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, p: '0 4px' } }}
                      >
                        <ApprovalsIcon fontSize="small" />
                      </Badge>
                    }
                    collapsed={collapsed}
                    isActive={isActive('/approvals')}
                    onClick={() => handleNav('/approvals')}
                    sc={sc}
                  />
                )}
              </List>
            </>

            {/* Zone 2: People & HR */}
            {(showPeople || showOnboarding || showRecruitment || showPerformance) && (
              <>
                {!collapsed && (
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'text.disabled', mt: 1.5, mb: 0.5, px: 2 }}>
                    People &amp; HR
                  </Typography>
                )}
                <List disablePadding>
                  <NavItem
                    label="People" path="/people" icon={<HRIcon fontSize="small" />}
                    collapsed={collapsed} isActive={isActive('/people')}
                    onClick={() => handleNav('/people')}
                    hasChildren={!collapsed && (showPeople || showOnboarding || showRecruitment || showPerformance)}
                    expanded={expandedSections.people}
                    onToggle={() => toggleSection('people')}
                    sc={sc}
                  />
                  {!collapsed && (
                    <Collapse in={expandedSections.people} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {showPeople && hasAnyPerm(['hr.employees.read', 'hr.employees.write']) && (
                          <NavItem label="Employees" path="/people#workforce.employees" icon={<DirectoryIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#workforce.employees')} onClick={() => handleNav('/people#workforce.employees', 'people')} isChild sc={sc} />
                        )}
                        {showPeople && hasAnyPerm(['hr.leave.read', 'hr.leave.write']) && (
                          <NavItem label="Leave" path="/people#management.leave" icon={<LeaveIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#management.leave')} onClick={() => handleNav('/people#management.leave', 'people')} isChild sc={sc} />
                        )}
                        {showOnboarding && (
                          <NavItem label="Onboarding" path="/people#hiring.onboarding" icon={<OnboardingIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#hiring.onboarding')} onClick={() => handleNav('/people#hiring.onboarding', 'people')} isChild sc={sc} />
                        )}
                        {showRecruitment && (
                          <NavItem label="Recruitment" path="/people#hiring.recruitment" icon={<RecruitmentIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#hiring.recruitment')} onClick={() => handleNav('/people#hiring.recruitment', 'people')} isChild sc={sc} />
                        )}
                        {showPerformance && (
                          <NavItem label="Performance" path="/people#management.performance" icon={<PerformanceIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/people#management.performance')} onClick={() => handleNav('/people#management.performance', 'people')} isChild sc={sc} />
                        )}
                      </List>
                    </Collapse>
                  )}
                </List>
              </>
            )}

            {/* Zone 3: Compensation */}
            {showPayroll && (
              <>
                {!collapsed && (
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'text.disabled', mt: 1.5, mb: 0.5, px: 2 }}>
                    Compensation
                  </Typography>
                )}
                <List disablePadding>
                  <NavItem label="Payroll" path="/payroll" icon={<PayrollIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/payroll')} onClick={() => handleNav('/payroll')} sc={sc} />
                </List>
              </>
            )}

            {/* Zone 4: Finance */}
            {(showFinance || showBudgetInFinance) && (
              <>
                {!collapsed && (
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'text.disabled', mt: 1.5, mb: 0.5, px: 2 }}>
                    Finance
                  </Typography>
                )}
                <List disablePadding>
                  <NavItem
                    label="Finance" path="/finance" icon={<LedgerIcon fontSize="small" />}
                    collapsed={collapsed} isActive={isActive('/finance')}
                    onClick={() => handleNav('/finance')}
                    hasChildren={!collapsed}
                    expanded={expandedSections.finance}
                    onToggle={() => toggleSection('finance')}
                    sc={sc}
                  />
                  {!collapsed && (
                    <Collapse in={expandedSections.finance} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {showFinance && hasAnyPerm(['accounting.revenue.read', 'accounting.revenue.write']) && (
                          <NavItem label="Revenue" path="/finance#transactions.revenue" icon={<RevenueIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#transactions.revenue')} onClick={() => handleNav('/finance#transactions.revenue', 'finance')} isChild sc={sc} />
                        )}
                        {showFinance && hasAnyPerm(['accounting.expense.read', 'accounting.expense.write']) && (
                          <NavItem label="Expenses" path="/finance#transactions.expenses" icon={<ExpenseIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#transactions.expenses')} onClick={() => handleNav('/finance#transactions.expenses', 'finance')} isChild sc={sc} />
                        )}
                        {showBudgetInFinance && (
                          <NavItem label="Budget" path="/finance#budget" icon={<BudgetIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#budget')} onClick={() => handleNav('/finance#budget', 'finance')} isChild sc={sc} />
                        )}
                        {hasAnyPerm(['billing.read']) && (
                          <NavItem label="Payments" path="/finance#payments" icon={<RevenueIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#payments')} onClick={() => handleNav('/finance#payments', 'finance')} isChild sc={sc} />
                        )}
                        {hasAnyPerm(['billing.read']) && (
                          <NavItem label="Payouts" path="/finance#payouts" icon={<RevenueIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/finance#payouts')} onClick={() => handleNav('/finance#payouts', 'finance')} isChild sc={sc} />
                        )}
                      </List>
                    </Collapse>
                  )}
                </List>
              </>
            )}

            {/* Zone 4b: Sales */}
            {showCRM && (
              <>
                {!collapsed && (
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'text.disabled', mt: 1.5, mb: 0.5, px: 2 }}>
                    Sales
                  </Typography>
                )}
                <List disablePadding>
                  <NavItem
                    label="CRM" path="/crm/contacts" icon={<CRMIcon fontSize="small" />}
                    collapsed={collapsed} isActive={isActive('/crm')}
                    onClick={() => handleNav('/crm/contacts')}
                    hasChildren={!collapsed}
                    expanded={expandedSections.crm}
                    onToggle={() => toggleSection('crm')}
                    sc={sc}
                  />
                  {!collapsed && (
                    <Collapse in={expandedSections.crm} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        <NavItem label="Contacts" path="/crm/contacts" icon={<ContactsIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/crm/contacts')} onClick={() => handleNav('/crm/contacts', 'crm')} isChild sc={sc} />
                        <NavItem label="Deals" path="/crm/kanban" icon={<KanbanIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/crm/kanban')} onClick={() => handleNav('/crm/kanban', 'crm')} isChild sc={sc} />
                      </List>
                    </Collapse>
                  )}
                </List>
              </>
            )}

            {/* Zone 5: Insights */}
            {(showAI || showReports) && (
              <>
                {!collapsed && (
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'text.disabled', mt: 1.5, mb: 0.5, px: 2 }}>
                    Insights
                  </Typography>
                )}
                <List disablePadding>
                  {showAI && (
                    <NavItem label="Intelligence" path="/intelligence" icon={<AIIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/intelligence')} onClick={() => handleNav('/intelligence')} sc={sc} />
                  )}
                  {showReports && (
                    <NavItem label="Reports" path="/reports" icon={<ReportsIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/reports')} onClick={() => handleNav('/reports')} sc={sc} />
                  )}
                </List>
              </>
            )}

            {/* Zone 6: Admin */}
            {(showFacility || showGroup || showSettings) && (
              <>
                {!collapsed && (
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'text.disabled', mt: 1.5, mb: 0.5, px: 2 }}>
                    Admin
                  </Typography>
                )}
                <List disablePadding>
                  {showFacility && (
                    <NavItem label="Facility" path="/facility" icon={<TenantIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/facility')} onClick={() => handleNav('/facility')} sc={sc} />
                  )}
                  {showGroup && (
                    <NavItem label="Organization" path="/group" icon={<GroupIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/group')} onClick={() => handleNav('/group')} tooltip={`${activeCompany?.group_name || 'Organization'}`} sc={sc} />
                  )}
                  {showSettings && (
                    <NavItem label="Settings" path="/settings" icon={<SettingsIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/settings')} onClick={() => handleNav('/settings')} sc={sc} />
                  )}
                </List>
              </>
            )}
          </>
        )}
      </Box>

      {/* Nav style toggle — flat vs grouped */}
      {!collapsed && (
        <>
          <Divider sx={{ borderColor: sc.divider }} />
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Flat layout" placement="top" arrow>
              <IconButton
                size="small"
                onClick={() => handleSetNavStyle('flat')}
                aria-label="Switch to flat nav layout"
                sx={{ color: navStyle === 'flat' ? 'primary.main' : 'text.disabled', p: 0.5 }}
              >
                <ViewListIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Grouped layout" placement="top" arrow>
              <IconButton
                size="small"
                onClick={() => handleSetNavStyle('grouped')}
                aria-label="Switch to grouped nav layout"
                sx={{ color: navStyle === 'grouped' ? 'primary.main' : 'text.disabled', p: 0.5 }}
              >
                <AccountTreeIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Help Center */}
      <Divider sx={{ borderColor: sc.divider }} />
      <List disablePadding sx={{ px: collapsed ? 0.5 : 1.5, py: 0.5 }}>
        <NavItem
          label="Help Center"
          path="/help-center"
          icon={<HelpCenterIcon fontSize="small" />}
          collapsed={collapsed}
          isActive={isActive('/help-center')}
          onClick={() => handleNav('/help-center')}
          sc={sc}
        />
      </List>

      {/* Profile at bottom */}
      <Divider sx={{ borderColor: sc.divider }} />
      <List disablePadding sx={{ px: collapsed ? 0.5 : 1.5, py: 1 }}>
        <NavItem label={t('nav.profile')} path="/profile" icon={<ProfileIcon fontSize="small" />} collapsed={collapsed} isActive={isActive('/profile')} onClick={() => handleNav('/profile')} sc={sc} />
      </List>
    </Box>
  );

  // Mobile: slide-in Drawer with backdrop. Tap outside or navigate to close.
  if (variant === 'temporary') {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }} // better mobile open/close perf
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_FULL,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        {navContent}
      </Drawer>
    );
  }

  // Desktop: permanent inline sidebar (current behaviour).
  return navContent;
}
